import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import BrandHero from "@/components/BrandHero";
import Row from "@/components/Row";
import MovieCard from "@/components/MovieCard";
import { Loader2, Star, Play, Info } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";

function FeaturePanel({ item }) {
  const navigate = useNavigate();
  if (!item) return null;
  return (
    <article
      data-testid={`feature-panel-${item.id}`}
      className="group relative rounded-sm overflow-hidden border border-white/10 hover:border-gold/50 transition-colors duration-500 min-h-[320px] lg:min-h-[440px] cursor-pointer"
      onClick={() => navigate(`/title/${item.id}`)}
    >
      <img src={item.backdrop_url} alt={item.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-r from-ink/70 to-transparent" />
      <div className="relative z-10 h-full flex flex-col justify-end p-6 sm:p-9">
        <span className="text-[11px] uppercase tracking-[0.25em] text-gold mb-2">
          {item.type === "series" ? "Featured Series" : "Featured Film"}
        </span>
        <h3 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl leading-none tracking-tight">{item.title}</h3>
        <div className="flex items-center gap-3 text-sm text-white/75 mt-3">
          <span className="text-gold flex items-center gap-1 font-medium"><Star className="w-4 h-4 fill-gold" strokeWidth={1.5} /> {item.rating}</span>
          <span>{item.release_year}</span>
          <span className="border border-white/25 px-1.5 rounded-sm text-xs">{item.maturity}</span>
          <span className="hidden sm:inline">{item.genres?.slice(0, 2).join(" • ")}</span>
        </div>
        <p className="text-white/70 text-sm sm:text-base leading-relaxed line-clamp-2 mt-3 max-w-xl">{item.description}</p>
        <div className="flex flex-wrap items-center gap-3 mt-5">
          <button
            data-testid={`feature-play-${item.id}`}
            onClick={(e) => { e.stopPropagation(); navigate(`/watch/${item.id}`); }}
            className="bg-gold text-ink hover:bg-gold-bright transition-colors rounded-sm font-medium px-6 py-2.5 flex items-center gap-2"
          >
            <Play className="w-5 h-5 fill-ink" strokeWidth={1.5} /> Watch Now
          </button>
          <span className="text-white/60 text-sm">
            {(Number(item.rent_price) <= 0 || Number(item.buy_price) <= 0)
              ? <span className="text-gold font-medium">Free to watch</span>
              : <>Rent <span className="text-gold font-medium">${item.rent_price ?? 3}</span> · Buy <span className="text-gold font-medium">${item.buy_price ?? 15}</span></>}
          </span>
        </div>
      </div>
    </article>
  );
}

function AdSlot({ ad }) {
  if (!ad) return null;
  const inner = (
    <div data-testid="home-ad-banner" className="relative border border-gold/20 bg-surface rounded-sm overflow-hidden">
      {ad.image_url ? (
        <img src={ad.image_url} alt={ad.name} className="w-full h-40 sm:h-52 object-cover" />
      ) : (
        <div className="h-32 flex items-center justify-center text-gold/70">{ad.name}</div>
      )}
      <span className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.2em] bg-black/60 border border-gold/30 text-gold px-2 py-0.5 rounded-sm">Sponsored</span>
    </div>
  );
  return (
    <section className="px-4 sm:px-10 lg:px-16 py-6">
      {ad.link_url ? <Link to={ad.link_url}>{inner}</Link> : inner}
    </section>
  );
}

export default function Home() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [extra, setExtra] = useState({ continue_watching: [], recommended: [] });
  const [ad, setAd] = useState(null);

  useSeo({
    description: "Rent or buy premium Burmese movies, TV series and documentaries on Mandalay+. Sign up free and watch instantly in HD & 4K.",
    keywords: "Mandalay+, Burmese movies, Myanmar films, rent movies, buy movies, Burmese TV series",
    image: "https://static.prod-images.emergentagent.com/jobs/ef1edcfe-cba8-4c9e-96d5-4167aceb4717/images/e2e79b19c82062db7c125ac7893cdbd15719754e44ad09b010703c56be2157b4.png",
  });

  useEffect(() => {
    api.get("/content").then((r) => setItems(r.data || [])).catch(() => setItems([]));
    api.get("/ads", { params: { placement: "home_banner" } })
      .then((r) => setAd((r.data || []).find((a) => a.active) || null)).catch(() => {});
    api.get("/home").then((r) => setExtra({
      continue_watching: r.data?.continue_watching || [],
      recommended: r.data?.recommended || [],
    })).catch(() => {});
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps -- refetch home data when auth state changes

  const addToList = async (item) => {
    if (!user) { toast.error("Sign in to add to your list"); return; }
    try {
      await api.post("/mylist", { content_id: item.id });
      toast.success(`Added "${item.title}" to My List`);
    } catch { toast.error("Could not add to list"); }
  };

  if (!items) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    );
  }

  const featured = items.find((c) => c.featured) || items[0];
  const rest = items.filter((c) => c.id !== featured?.id);
  const sideTwo = rest.slice(0, 2);
  const below = rest.slice(2);

  return (
    <div className="bg-ink min-h-screen pb-24">
      <BrandHero featured={featured} />

      <section className="px-4 sm:px-10 lg:px-16 py-14 sm:py-20">
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <span className="text-[11px] uppercase tracking-[0.3em] text-gold">Curated Selection</span>
            <h2 className="font-display font-semibold text-3xl sm:text-4xl tracking-tight mt-2">Now Showing</h2>
          </div>
          <Link to="/browse" data-testid="home-browse-all" className="text-sm text-white/60 hover:text-gold transition-colors shrink-0">
            View all →
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          <div className="lg:col-span-7">
            <FeaturePanel item={featured} />
          </div>
          <div className="lg:col-span-5 grid grid-cols-2 gap-5">
            {sideTwo.map((it) => (
              <MovieCard key={it.id} item={it} onAdd={addToList} fluid />
            ))}
          </div>
        </div>

        {below.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5 mt-5">
            {below.map((it) => (
              <MovieCard key={it.id} item={it} onAdd={addToList} fluid />
            ))}
          </div>
        )}
      </section>

      <AdSlot ad={ad} />

      {extra.continue_watching.length > 0 && (
        <Row title="Continue Watching" items={extra.continue_watching} onAdd={addToList} />
      )}
      {extra.recommended.length > 0 && (
        <Row title="Recommended for You" items={extra.recommended} onAdd={addToList} />
      )}
    </div>
  );
}
